import {
  BusStatus,
  DistanceUnit,
  DriverAccommodationType,
  DriverStatus,
  LanguageOptions,
  PaymentMethod,
  RideOfferType,
  SpecialLuggageType,
  VehicleClass,
  VehicleExtrasEnum,
  clientDtoSchema,
  contactInfoDtoSchema,
  driverAccommodationTypeObjectSchema,
  groupLeadSchema,
  legSchema,
  orderFilesSchemaObject,
  passengerCountSchemaObject,
  passengerCountWithMaxSchemaObject,
  rideLegsSchema,
  specialLuggageObjectSchema,
  zo,
} from 'remote/global';
import { clientBaseSchema } from '../AccountFactory/types';
import { vehiclePayloadSchema } from '../FleetFormFactory/types';
import { driverAccommodationSchemaObject } from '../RideFormFactory/types';
import { scheduleItemDtoSchema } from '../types';

const orderDetailsSchema = zo.object({
  driverAccommodationType: driverAccommodationSchemaObject.or(zo.literal('')),
  internalInfo: zo.string().min(1).max(256).optional().or(zo.literal('')),
  passengerCount: passengerCountSchemaObject,
  pickUpLocationMessage: zo.string().min(1).max(256).optional().or(zo.literal('')),
  signText: zo.string().min(1).max(256).optional().or(zo.literal('')),
  specialLuggageCount: zo.coerce
    .string()
    .min(1)
    .pipe(zo.coerce.number().int().gte(1).lte(5))
    .or(zo.literal(''))
    .optional(),
  specialLuggageType: zo.nativeEnum(SpecialLuggageType).or(zo.literal('')).optional(),
  overnight: zo.boolean(), // This is used internally to check if driverAccommodationType is required
});

const orderDetailsPayloadSchema = groupLeadSchema.merge(orderDetailsSchema);

const bookingExtrasSchema = passengerCountWithMaxSchemaObject
  .and(driverAccommodationTypeObjectSchema)
  .and(orderFilesSchemaObject)
  .and(specialLuggageObjectSchema)
  .and(
    zo.object({
      internalInfo: zo.string().min(1).max(256).optional().or(zo.literal('')),
      pickUpLocationMessage: zo.string().min(1).max(256).optional().or(zo.literal('')),
      signText: zo.string().min(1).max(256).optional().or(zo.literal('')),
    })
  );

const rideDriverSchema = zo.object({
  contactInfo: contactInfoDtoSchema,
  hasAccount: zo.boolean().optional(),
  id: zo.number(),
  languages: zo.array(zo.nativeEnum(LanguageOptions)),
  operatorId: zo.number(),
});

const rideVehicleSchema = zo.intersection(
  vehiclePayloadSchema,
  zo.object({
    id: zo.number(),
  })
);

const rideSchema = rideLegsSchema.merge(
  zo.object({
    bookingNumber: zo.string(),
    driver: rideDriverSchema,
    id: zo.number(),
    operatorId: zo.number(),
    priceExclVat: zo.coerce.number(),
    priceInclVat: zo.coerce.number(),
    rideNumber: zo.number(),
    vatPct: zo.coerce.number(),
    vehicle: rideVehicleSchema,
  })
);

const driverDtoSchema = zo.object({
  contactInfo: contactInfoDtoSchema,
  driverPhotoUrl: zo.string().optional(),
  employmentContractNo: zo.string().optional(),
  hasAccount: zo.boolean(),
  id: zo.number(),
  idCode: zo.string().optional(),
  languages: zo.array(zo.nativeEnum(LanguageOptions)),
  operatorId: zo.number(),
});

const driverWithStatusDtoSchema = driverDtoSchema.extend({
  busyMinutesMtd: zo.number(),
  currentScheduleItem: scheduleItemDtoSchema.optional(),
  hasAllDocuments: zo.boolean(),
  rideCountMtd: zo.number(),
  status: zo.nativeEnum(DriverStatus),
  vehicleId: zo.number().optional(),
  vehicleLicensePlate: zo.string().optional(),
  workdayEnd: zo.string().datetime().optional(),
  workdayMinutes: zo.number(),
  workdayStart: zo.string().datetime().optional(),
});
type DriverWithStatusDtoSchemaType = zo.infer<typeof driverWithStatusDtoSchema>;

const coordsSchema = zo.object({
  lat: zo.number(),
  lon: zo.number(),
});

const locationDtoSchema = zo.object({
  address: zo.string(),
  coords: coordsSchema.optional(),
  countryCode: zo.string(),
  placeId: zo.string().optional(),
});

const vehicleDtoSchema = zo.object({
  availableForOffer: zo.boolean(),
  defaultDriverId: zo.number(),
  defaultLocation: locationDtoSchema,
  extras: zo.array(zo.nativeEnum(VehicleExtrasEnum)),
  id: zo.number(),
  lengthMeters: zo.number(),
  licensePlate: zo.string(),
  make: zo.string(),
  mileage: zo.number().optional(),
  mileageUnit: zo.nativeEnum(DistanceUnit),
  model: zo.string(),
  operatorId: zo.number(),
  seats: zo.number(),
  standingPlaces: zo.number(),
  vehicleClass: zo.nativeEnum(VehicleClass),
  year: zo.number(),
});

const vehicleWithStatusDtoSchema = vehicleDtoSchema.extend({
  busyMinutesMtd: zo.number(),
  busyMinutesYtd: zo.number(),
  currentDriverId: zo.number().optional(),
  currentDriverName: zo.string().optional(),
  currentScheduleItem: scheduleItemDtoSchema.optional(),
  hasAllDocuments: zo.boolean(),
  hasAnyPhotos: zo.boolean(),
  rideCountMtd: zo.number(),
  rideCountYtd: zo.number(),
  status: zo.nativeEnum(BusStatus).optional(),
});
type VehicleWithStatusDtoSchema = zo.infer<typeof vehicleWithStatusDtoSchema>;

const rideDtoSchema = zo.object({
  bookingNumber: zo.string(),
  confirmedAt: zo.string().datetime().optional(),
  driver: driverDtoSchema,
  finishedAt: zo.string().datetime().optional(),
  id: zo.number(),
  legs: zo.array(legSchema),
  operatorId: zo.number(),
  pausedAt: zo.string().datetime().optional(),
  priceExclVat: zo.number(),
  priceInclVat: zo.number(),
  rideNumber: zo.number(),
  startedAt: zo.string().datetime().optional(),
  vatPct: zo.number(),
  vehicle: vehicleDtoSchema,
  warningCode: zo.array(zo.string()), // This is actually an enum in BE
});
type RideDtoSchemaType = zo.infer<typeof rideDtoSchema>;

const operatorRideDtoSchema = zo.object({
  bookingNumber: zo.string(),
  clientName: zo.string().optional(),
  createdAt: zo.string().datetime(),
  driverId: zo.number().optional(),
  driverName: zo.string().optional(),
  estimatedEndAt: zo.string().datetime(),
  licensePlate: zo.string(),
  make: zo.string(),
  mileageKm: zo.number().optional(),
  model: zo.string(),
  orderId: zo.number(),
  passengerCount: zo.number(),
  rideId: zo.number(),
  rideNumber: zo.number(),
  seats: zo.number(),
  startedAt: zo.string().datetime(),
  vehicleId: zo.number(),
});
type OperatorRideDtoSchemaType = zo.infer<typeof operatorRideDtoSchema>;

const orderBaseSchema = orderDetailsPayloadSchema.merge(
  zo.object({
    id: zo.number(),
    operatorId: zo.number(),
    paymentMethod: zo.nativeEnum(PaymentMethod),
    type: zo.nativeEnum(RideOfferType),
    client: clientBaseSchema,
    rides: zo.array(rideSchema),
  })
);

const orderDtoSchema = zo.object({
  bookingNumber: zo.string(),
  cancelledAt: zo.string().datetime().optional(),
  client: clientDtoSchema.optional(),
  createdAt: zo.string().datetime().optional(),
  currency: zo.string(),
  driverAccommodationType: zo.nativeEnum(DriverAccommodationType),
  groupLead: groupLeadSchema.shape.groupLead.optional(),
  id: zo.number(),
  internalInfo: zo.string().optional(),
  operatorId: zo.number(),
  operatorLogoUrl: zo.string().optional(),
  operatorName: zo.string(),
  operatorPhoneNumber: zo.string().optional(),
  operatorPriceExclVat: zo.number(),
  operatorPriceInclVat: zo.number(),
  orderedByContactInfo: contactInfoDtoSchema.optional(),
  overnight: zo.boolean(),
  passengerCount: zo.boolean(),
  paymentMethod: zo.nativeEnum(PaymentMethod),
  pickUpLocationMessage: zo.string().optional(),
  priceExclVat: zo.number(),
  priceInclVat: zo.number(),
  rideDetailsFileUrl: zo.string().optional(),
  rides: zo.array(rideDtoSchema),
  signFileUrl: zo.string().optional(),
  signText: zo.string().optional(),
  specialLuggageCount: zo.number(),
  specialLuggageType: zo.number(),
  type: zo.nativeEnum(RideOfferType),
  unadjustedPriceExclVat: zo.number(),
  unadjustedPriceInclVat: zo.number(),
  clientListPriceInclVat: zo.number(),
  clientListPriceExclVat: zo.number(),
  vatPct: zo.number(),
});
type OrderDtoSchemaType = zo.infer<typeof orderDtoSchema>;

type OrderDetailsSchemaPayloadType = zo.infer<typeof orderDetailsPayloadSchema>;
type BookingExtrasSchemaType = zo.infer<typeof bookingExtrasSchema>;
type RideSchemaType = zo.infer<typeof rideSchema>;
type OrderSchemaType = zo.infer<typeof orderBaseSchema>;
type DriverDtoSchema = zo.infer<typeof driverDtoSchema>;
type VehicleDtoSchema = zo.infer<typeof vehicleDtoSchema>;

export {
  orderBaseSchema,
  bookingExtrasSchema,
  rideSchema,
  rideDtoSchema,
  driverWithStatusDtoSchema,
  operatorRideDtoSchema,
};
export type {
  BookingExtrasSchemaType,
  OrderDetailsSchemaPayloadType,
  RideSchemaType,
  OrderSchemaType,
  RideDtoSchemaType,
  OrderDtoSchemaType,
  DriverDtoSchema,
  VehicleDtoSchema,
  DriverWithStatusDtoSchemaType,
  VehicleWithStatusDtoSchema,
  OperatorRideDtoSchemaType,
};
